<template>
    <b-modal
        title-tag="h2"
        title="Email Letter"
        lazy
        :visible="isOpen"
        no-enforce-focus
    >
        <form>
            <b-form-group label-cols="2" label="To:" label-for="to">
                <!-- do not change the module of vet select: logic handling issue -->
                <recipient-email
                    name="recipient"
                    v-model="recipient"
                    multiple
                    placeholder="Client or Contact Email (required)"
                    @input="values => handleRecipientChangeAlt(values, true)"
                    :key="recipientComponentKey"
                    :client="clientDetails"
                    :filterData="false"
                />
            </b-form-group>

            <b-form-group label-cols="2" label="Subject:" label-for="subject">
                <b-form-input
                    v-model="subject"
                    :placeholder="
                        isLoadingEmailDefaultLetter
                            ? 'Loading...'
                            : 'Subject (optional)'
                    "
                />
            </b-form-group>

            <b-form-group label-cols="2" label="Message:" label-for="message">
                <tinymce
                    :init="tinymceConfig"
                    v-model="message"
                    @onKeyPress="contentChanged"
                />
            </b-form-group>
        </form>

        <template v-slot:modal-footer>
            <loader v-if="isSending" class="m-0" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import ClientDocumentService from "@/services/client-documents";
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import recipientEmail from "@/components/commons/recipients-email";
import EmailMixins from "@/mixins/email";

export default {
    components: {
        Loader,
        tinymce,
        recipientEmail
    },
    mixins: [EmailMixins],
    data() {
        return {
            isOpen: false,
            letterId: null,
            recipient: [],
            subject: "",
            message: "",
            isLoading: true,
            isSending: false,
            isLoadingEmailDefaultLetter: true,
            clientDetails: null,
            recipientComponentKey: 0,
            emailDefaultLetter: {
                body: null,
                subject: null
            },
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            isContentChanged: false,
            isLoadingModal: true
        };
    },
    methods: {
        contentChanged() {
            // detect content has been changed by user (once)
            if (!this.isContentChanged) {
                this.isContentChanged = true;
            }
            return true;
        },
        async open(record) {
            this.isOpen = true;
            this.letterId = record.id;
            this.isLoadingModal = true;
            await this.fetchSetEmailDefaultLetter(record);
            this.setDefaultRecipient(record.recipient, record);
            this.isLoadingModal = false;
        },
        close() {
            this.isOpen = false;
        },
        setDefaultRecipient(recipient = {}, record) {
            if (recipient) {
                recipient.recipient_is_client = record.recipient_is_client;
                recipient.recipient_type = record.recipient_type;
            }
            this.clientDetails = recipient;
            this.recipientComponentKey++; // force re-render // Allow the field to pre-select the recipient
        },
        constructActorIds(recipients = []) {
            let ids = [];

            for (const recipient of recipients) {
                ids.push(recipient.to_id);
            }

            return ids;
        },
        async send() {
            const firstRecipient = this.recipient[0];

            if (!firstRecipient || this.message === "") {
                return;
            }

            this.isSending = true;

            const { firstname, lastname } = firstRecipient;

            const successSent = await ClientDocumentService.sendEmail({
                letterId: this.letterId,
                to: this.constructActorIds(this.recipient),
                firstname,
                lastname,
                subject: this.subject,
                message: this.message,
                timestamp: this._now().toFormat("X")
            });

            this.isSending = false;

            if (successSent) {
                this.close();
                this._message("Letter emailed");
            } else {
                this._message("Error: Unable to send email", "danger");
            }
        },
        async fetchSetEmailDefaultLetter(record) {
            this.isLoadingEmailDefaultLetter = true;

            const formData = new FormData();
            formData.append("params[0][email_type]", 11);
            formData.append("params[0][business_id]", this._setting.business.id); // prettier-ignore
            formData.append("params[0][client_id]", record.client_id);
            formData.append("params[0][patient_id][]", record.patient_id ? [record.patient_id] : []); // prettier-ignore

            formData.append("params[0][letter_id]", record.id);
            formData.append("params[0][letter_template]", record.template_id);
            formData.append("params[0][letter_title]", record.title);
            formData.append("params[0][skip_variable_replacement]", true);

            const res = await this.$http.post(
                `${process.env.CI_APPS}common/appointment/getEmailTemplate`,
                formData
            );

            let template = {};
            if (res.data && res.data.response && res.data.response[0]) {
                template = res.data.response[0].email_template;
            }

            this.emailDefaultLetter = {
                body: template.message,
                subject: template.subject
            };

            this.isLoadingEmailDefaultLetter = false;
            this.subject = template.subject;
            this.message = this.setNamesElement(
                this.emailDefaultLetter.body,
                false
            );
        },
        findElements(htmlContent) {
            // VET-5629 - Issue with HTML tag showing after changed client
            // Previous code basically slicing header from body based on  <p></p>
            // When there is not <p></p>, the last index would be -1, causing any last letter to be cut.
            // for example, <h1>example</h1>, thus it will become <h1>example</h1

            // Creating a DOMParser instance
            const parser = new DOMParser();

            // Parsing the HTML string into a document
            const doc = parser.parseFromString(htmlContent, "text/html");

            // Accessing the first element in the body of the parsed HTML
            const firstElement = doc.body.firstElementChild;

            let restContent = "";
            let sibling = firstElement.nextSibling;

            while (sibling) {
                restContent += sibling.outerHTML || sibling.textContent;
                sibling = sibling.nextSibling;
            }

            return {
                header: firstElement.outerHTML,
                body: restContent
            };
        },
        toHandleRecipientChange(values = []) {
            if (this.isLoadingModal) {
                return; // Skip if still loading the modal on first time initialization
            } else {
                this.handleRecipientChangeAlt(values);
            }
        }
    }
};
</script>
